"use client";

import React from "react";
import {
  CheckCircle,
  Info,
  Warning,
  WarningCircle,
} from "@phosphor-icons/react";
import { useTranslate } from "../../lib/translation";
import styles from "./HelperText.module.css";

export type HelperTextState = "error" | "warning" | "success" | "info";

export interface HelperTextProps {
  /**
   * The helper text content to display
   */
  children: React.ReactNode;
  /**
   * Optional ID for aria-describedby association
   */
  id?: string;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Semantic state of the helper text (error, warning, success, info).
   * Warning, success and info icons carry a translated text alternative, so
   * the state is not conveyed by colour alone. Default is neutral/no state.
   */
  state?: HelperTextState;
}

/** Word read before the copy so the state is not conveyed by colour alone. */
const stateWordKeys = {
  warning: ["helperText.state.warning", "Warning"],
  success: ["helperText.state.success", "Success"],
  info: ["helperText.state.info", "Info"],
} as const;

const stateIcons: Record<HelperTextState, React.ReactNode> = {
  error: <WarningCircle weight="fill" size={16} />,
  warning: <Warning weight="fill" size={16} />,
  success: <CheckCircle weight="fill" size={16} />,
  info: <Info weight="fill" size={16} />,
};

/** Semantic helper copy for form fields (error, warning, success, info). */
const HelperText = React.forwardRef<HTMLParagraphElement, HelperTextProps>(
  ({ children, id, className, state }, ref) => {
    const t = useTranslate();
    const mergedClassName = [
      styles.helperText,
      state && styles[state],
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const icon = state ? stateIcons[state] : null;
    // Error copy states the problem and is announced by role="alert"; the
    // other states name themselves through the icon's text alternative.
    const stateWord =
      state && state !== "error"
        ? t(stateWordKeys[state][0], stateWordKeys[state][1])
        : undefined;

    return (
      <p
        ref={ref}
        id={id}
        className={mergedClassName}
        role={state === "error" ? "alert" : undefined}
      >
        {icon && (
          <span className={styles.icon}>
            {icon}
            {stateWord ? (
              <span className={styles.srOnly}>{`${stateWord}:`}</span>
            ) : null}
          </span>
        )}
        {/* Separates the state word from the copy in the accessible text;
            whitespace between flex items does not render. */}
        {stateWord ? " " : null}
        {children}
      </p>
    );
  },
);

HelperText.displayName = "HelperText";

export default HelperText;
